import React, { useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  Grid, IconButton, MenuItem, Paper, RadioGroup, FormControlLabel, Radio,
  Autocomplete, TextField, Typography, alpha, Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetAppReleaseQuery, useGetDeployTargetsQuery, useDeployAppReleaseMutation,
  usePauseAppReleaseMutation, useResumeAppReleaseMutation,
  useRetryFailedAppReleaseMutation, useDeactivateAppReleaseMutation,
} from "../../Api/attoDeskApi";
import { useNotifier } from "../../Core/Notifier";
import PageLoader from "../../Components/Loader/PageLoader";
import { NAVY, GOLD, GOLD_SOFT, BORDER, TEXT_SECONDARY } from "../../theme/tokens";
import {
  formatBytes, fmtDate, CHANNEL_META, DEPLOY_TARGETS, POLICIES, INSTALL_MODES, ROLLOUT_STAGES,
} from "./updateShared";

const StatTile: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 2, textAlign: "center" }}>
    <Typography sx={{ fontSize: 26, fontWeight: 800, color }}>{value}</Typography>
    <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</Typography>
  </Paper>
);

const Field: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({ label, value, mono }) => (
  <Grid item xs={12} sm={6}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, color: NAVY, fontWeight: 600, fontFamily: mono ? "monospace" : undefined, wordBreak: "break-all" }}>
      {value ?? "—"}
    </Typography>
  </Grid>
);

const ReleaseDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const releaseId = Number(id);
  const { showMessage, showErrorMessage } = useNotifier();

  const { data, isLoading, refetch } = useGetAppReleaseQuery(releaseId);
  const [deploy, { isLoading: deploying }] = useDeployAppReleaseMutation();
  const [pause] = usePauseAppReleaseMutation();
  const [resume] = useResumeAppReleaseMutation();
  const [retryFailed] = useRetryFailedAppReleaseMutation();
  const [deactivate] = useDeactivateAppReleaseMutation();
  const { data: targetsData } = useGetDeployTargetsQuery();

  const [deployOpen, setDeployOpen] = useState(false);
  const [dTarget, setDTarget] = useState("all_active");
  const [dPolicy, setDPolicy] = useState("optional");
  const [dMode, setDMode] = useState("maintenance_window");
  const [dStage, setDStage] = useState("100");
  const [dCompanies, setDCompanies] = useState<number[]>([]);
  const [dGroups, setDGroups] = useState<number[]>([]);

  if (isLoading) return <PageLoader height="20vh" />;
  const r: any = data?.data;
  if (!r) return <Box sx={{ p: 4 }}><Typography>Release not found.</Typography></Box>;

  const stats = r.stats || { assigned: 0, successful: 0, pending: 0, failed: 0, offline: 0 };
  const cc = CHANNEL_META[r.release_channel] || CHANNEL_META.stable;
  const companies: Array<{ id: number; name: string; active_devices: number }> = (targetsData?.data as any)?.companies ?? [];
  const groups: Array<{ id: number; name: string; device_count: number }> = (targetsData?.data as any)?.groups ?? [];

  const act = async (fn: () => Promise<any>, ok: string) => {
    try { const res: any = await fn(); if (res?.data?.status ?? res?.status ?? true) { showMessage(ok); refetch(); } }
    catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  const runDeploy = async () => {
    const pct = ROLLOUT_STAGES.find((s) => s.value === dStage)?.pct ?? 100;
    const body: any = {
      deploy_target: dTarget, policy: dPolicy, install_mode: dMode,
      rollout_stage: dStage, rollout_percentage: pct,
    };
    if (dTarget === "companies") body.target_ids = dCompanies;
    if (dTarget === "groups") body.target_ids = dGroups;
    try {
      const res: any = await deploy({ id: releaseId, body }).unwrap();
      if (res.status) { showMessage(res.message || "Deployed"); setDeployOpen(false); refetch(); }
      else showErrorMessage(res.message || "Deploy failed");
    } catch (e: any) { showErrorMessage(e?.data?.message || "Deploy failed"); }
  };

  const targets = r.targets || {};
  const targetChips: React.ReactNode[] = [];
  if (targets.all_active) targetChips.push(<Chip key="all" size="small" label="All active devices" sx={{ m: 0.25 }} />);
  Object.values(targets.companies || {}).forEach((n: any, i) => targetChips.push(<Chip key={`c${i}`} size="small" label={`🏢 ${n}`} sx={{ m: 0.25 }} />));
  Object.values(targets.groups || {}).forEach((n: any, i) => targetChips.push(<Chip key={`g${i}`} size="small" label={`👥 ${n}`} sx={{ m: 0.25 }} />));
  Object.values(targets.devices || {}).forEach((n: any, i) => targetChips.push(<Chip key={`d${i}`} size="small" label={`📺 ${n}`} sx={{ m: 0.25 }} />));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <IconButton size="small" onClick={() => navigate("/app-updates")}><ArrowBackIcon /></IconButton>
        <Typography sx={{ fontSize: 20, fontWeight: 800, color: NAVY }}>
          {r.version_name} <Typography component="span" sx={{ fontSize: 13, color: TEXT_SECONDARY, fontFamily: "monospace" }}>code {r.version_code}</Typography>
        </Typography>
        <Chip size="small" label={r.release_channel.toUpperCase()} sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: cc.bg, color: cc.fg }} />
        {!r.is_active && <Chip size="small" label="INACTIVE" sx={{ height: 20, fontSize: 10, bgcolor: "#f1f5f9", color: "#64748b" }} />}
        <Box sx={{ flex: 1 }} />
      </Box>

      {/* Rollout stats */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm><StatTile label="Assigned" value={stats.assigned} color={NAVY} /></Grid>
        <Grid item xs={6} sm><StatTile label="Successful" value={stats.successful} color="#047857" /></Grid>
        <Grid item xs={6} sm><StatTile label="Pending" value={stats.pending} color="#1d4ed8" /></Grid>
        <Grid item xs={6} sm><StatTile label="Failed" value={stats.failed} color="#b91c1c" /></Grid>
        <Grid item xs={6} sm><StatTile label="Offline" value={stats.offline} color="#64748b" /></Grid>
      </Grid>

      {/* Deployment actions */}
      <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 2, mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button size="small" variant="contained" disableElevation onClick={() => setDeployOpen(true)}
          sx={{ textTransform: "none", fontWeight: 700, background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`, color: "#0b1e2d" }}>
          Deploy to more devices
        </Button>
        <Button size="small" variant="outlined" onClick={() => act(() => pause(releaseId).unwrap(), "Rollout paused")} sx={{ textTransform: "none" }}>Pause rollout</Button>
        <Button size="small" variant="outlined" onClick={() => act(() => resume(releaseId).unwrap(), "Rollout resumed")} sx={{ textTransform: "none" }}>Resume rollout</Button>
        <Button size="small" variant="outlined" onClick={() => act(() => retryFailed(releaseId).unwrap(), "Retrying failed devices")} sx={{ textTransform: "none" }}>Retry failed devices</Button>
        <Button size="small" variant="outlined" color="error" onClick={() => act(() => deactivate(releaseId).unwrap(), "Release deactivated")} sx={{ textTransform: "none" }}>Deactivate release</Button>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 2.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: NAVY, mb: 1.5 }}>Release information</Typography>
            <Grid container spacing={2}>
              <Field label="Version name" value={r.version_name} />
              <Field label="Version code" value={r.version_code} />
              <Field label="Platform" value={r.platform} />
              <Field label="Channel" value={r.release_channel} />
              <Field label="APK filename" value={r.apk_original_name || "—"} />
              <Field label="File size" value={formatBytes(r.file_size)} />
              <Field label="Package name" value={r.package_name || "—"} />
              <Field label="Min Android API" value={r.min_sdk_version || "—"} />
              <Field label="SHA-256 checksum" value={r.checksum || "—"} mono />
              <Field label="Uploaded by" value={r.uploaded_by || "—"} />
              <Field label="Uploaded" value={fmtDate(r.created_at)} />
              <Field label="Force / mandatory" value={r.force_update ? "Yes" : "No"} />
            </Grid>
            {r.release_notes && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase" }}>Release notes</Typography>
                <Typography sx={{ fontSize: 13, color: NAVY, whiteSpace: "pre-wrap" }}>{r.release_notes}</Typography>
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 2.5, mb: 2 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: NAVY, mb: 1 }}>Targets</Typography>
            {targetChips.length ? <Box>{targetChips}</Box> :
              <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>Not deployed yet.</Typography>}
          </Paper>

          <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 2.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: NAVY, mb: 1 }}>Deployments</Typography>
            {(r.deployments || []).length === 0 ? (
              <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>No deployments.</Typography>
            ) : (r.deployments || []).map((d: any) => (
              <Box key={d.id} sx={{ py: 1, borderBottom: `1px solid ${BORDER}` }}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                  <Chip size="small" label={d.target_type} sx={{ height: 20, fontSize: 10 }} />
                  <Chip size="small" label={d.policy} sx={{ height: 20, fontSize: 10 }} />
                  <Chip size="small" label={`${d.rollout_stage} (${d.rollout_percentage}%)`} sx={{ height: 20, fontSize: 10 }} />
                  <Chip size="small" label={d.status}
                    sx={{ height: 20, fontSize: 10, bgcolor: d.status === "paused" ? "#fff7ed" : "#ecfdf5", color: d.status === "paused" ? "#c2410c" : "#047857" }} />
                </Box>
                <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 0.5 }}>
                  {d.install_mode} · {fmtDate(d.created_at)} · by {d.created_by || "—"}
                  {d.failure_threshold_percent ? ` · auto-pause @ ${d.failure_threshold_percent}% fail` : ""}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>

      {/* Deploy-to-more dialog */}
      <Dialog open={deployOpen} onClose={() => setDeployOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 800, color: NAVY }}>Deploy to more devices</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
            <TextField select size="small" label="Deploy to" value={dTarget} onChange={(e) => setDTarget(e.target.value)}>
              {DEPLOY_TARGETS.filter((t) => t.value !== "none" && t.value !== "devices").map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
            {dTarget === "companies" && (
              <Autocomplete multiple size="small" options={companies}
                getOptionLabel={(o) => `${o.name} (${o.active_devices} active)`}
                value={companies.filter((c) => dCompanies.includes(c.id))}
                onChange={(_e, v) => setDCompanies(v.map((c) => c.id))}
                renderInput={(p) => <TextField {...p} label="Companies" />} />
            )}
            {dTarget === "groups" && (
              <Autocomplete multiple size="small" options={groups}
                getOptionLabel={(o) => `${o.name} (${o.device_count} devices)`}
                value={groups.filter((g) => dGroups.includes(g.id))}
                onChange={(_e, v) => setDGroups(v.map((g) => g.id))}
                renderInput={(p) => <TextField {...p} label="Device groups" />} />
            )}
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY }}>POLICY</Typography>
              <RadioGroup row value={dPolicy} onChange={(e) => setDPolicy(e.target.value)}>
                {POLICIES.map((p) => <FormControlLabel key={p.value} value={p.value} control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: 12 }}>{p.label}</Typography>} />)}
              </RadioGroup>
            </Box>
            <TextField select size="small" label="Schedule" value={dMode} onChange={(e) => setDMode(e.target.value)}>
              {INSTALL_MODES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Staged rollout" value={dStage} onChange={(e) => setDStage(e.target.value)}>
              {ROLLOUT_STAGES.filter((s) => s.value !== "custom").map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </TextField>
            {dPolicy === "mandatory" && dTarget === "all_active" && (
              <Alert severity="warning">This deploys a mandatory update to all active devices.</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeployOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" disableElevation onClick={runDeploy} disabled={deploying}
            sx={{ textTransform: "none", fontWeight: 700, background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`, color: "#0b1e2d" }}>
            {deploying ? "Deploying…" : "Deploy"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReleaseDetails;
